import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Switch,
  Typography,
  Box,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { gridSizeMdLg6 } from '@/shared';
import {
  ITicketTipo,
  ITicketCategoria,
  ITicketArea,
} from '@/shared/interfaces/erp/tickets-soporte-clientes/gestion-ticket.interface';

type CatalogType =
  | 'estados'
  | 'prioridades'
  | 'areas'
  | 'canales'
  | 'tipos'
  | 'categorias'
  | 'subcategorias';

interface FormCatalogoProps {
  open: boolean;
  onClose: () => void;
  catalogType: CatalogType;
  initialData?: any;
  onSubmit: (data: any) => void;
  isLoading: boolean;
  tipos?: ITicketTipo[];
  categorias?: ITicketCategoria[];
  /**
   * Areas disponibles para asociar al Tipo. Si el usuario es admin global
   * vienen todas; si es supervisor/responsable solo las que gestiona.
   * Si la lista esta vacia el field "area" se oculta del form.
   */
  areas?: ITicketArea[];
  /**
   * Valores iniciales extra SOLO para modo crear (initialData null): se
   * funden sobre los defaults del catalogo. El arbol unificado lo usa para
   * precargar el padre (tipo/categoria) y el area seleccionada.
   */
  prefill?: Record<string, any>;
}

interface FieldConfig {
  name: string;
  label: string;
  type: 'text' | 'number' | 'color' | 'switch' | 'select';
  required?: boolean;
  options?: { label: string; value: string }[];
}

const CATALOG_LABELS: Record<CatalogType, string> = {
  estados: 'Estado',
  prioridades: 'Prioridad',
  areas: 'Area',
  canales: 'Canal',
  tipos: 'Tipo',
  categorias: 'Categoria',
  subcategorias: 'Subcategoria',
};

const getFieldsConfig = (
  catalogType: CatalogType,
  tipos: ITicketTipo[],
  categorias: ITicketCategoria[],
  areas: ITicketArea[] = [],
): FieldConfig[] => {
  switch (catalogType) {
    case 'estados':
      return [
        { name: 'codigo', label: 'Codigo', type: 'text', required: true },
        { name: 'nombre', label: 'Nombre', type: 'text', required: true },
        { name: 'color', label: 'Color', type: 'color' },
        { name: 'icono', label: 'Icono', type: 'text' },
        { name: 'orden', label: 'Orden', type: 'number' },
        { name: 'es_inicial', label: 'Es Inicial', type: 'switch' },
        { name: 'es_final', label: 'Es Final', type: 'switch' },
        { name: 'activo', label: 'Activo', type: 'switch' },
      ];
    case 'prioridades':
      return [
        { name: 'codigo', label: 'Codigo', type: 'text', required: true },
        { name: 'nombre', label: 'Nombre', type: 'text', required: true },
        { name: 'sla_minutos', label: 'SLA (minutos)', type: 'number', required: true },
        { name: 'color', label: 'Color', type: 'color' },
        { name: 'icono', label: 'Icono', type: 'text' },
        { name: 'orden', label: 'Orden', type: 'number' },
      ];
    case 'areas':
      return [
        { name: 'codigo', label: 'Codigo', type: 'text', required: true },
        { name: 'nombre', label: 'Nombre', type: 'text', required: true },
        { name: 'descripcion', label: 'Descripcion', type: 'text' },
        { name: 'color', label: 'Color', type: 'color' },
        { name: 'orden', label: 'Orden', type: 'number' },
        { name: 'activa', label: 'Activa', type: 'switch' },
      ];
    case 'canales':
      return [
        { name: 'codigo', label: 'Codigo', type: 'text', required: true },
        { name: 'nombre', label: 'Nombre', type: 'text', required: true },
        { name: 'icono', label: 'Icono', type: 'text' },
        { name: 'activo', label: 'Activo', type: 'switch' },
      ];
    case 'tipos':
      return [
        // Area opcional: si no se selecciona, el tipo queda como "legacy"
        // (visible para todas las areas). Solo se muestra si hay areas en
        // la lista (admins globales ven todas, supervisores solo las suyas).
        ...(areas.length > 0
          ? [{
              name: 'area_id',
              label: 'Area (opcional, sino visible para todas)',
              type: 'select' as const,
              options: areas.map((a) => ({
                label: a.nombre,
                value: a.id_area,
              })),
            }]
          : []),
        { name: 'codigo', label: 'Codigo', type: 'text', required: true },
        { name: 'nombre', label: 'Nombre', type: 'text', required: true },
        { name: 'descripcion', label: 'Descripcion', type: 'text' },
        { name: 'requiere_visita_default', label: 'Requiere Visita por Defecto', type: 'switch' },
        { name: 'activo', label: 'Activo', type: 'switch' },
        { name: 'aplica_masivos', label: 'Aplica para Masivos', type: 'switch' },
      ];
    case 'categorias':
      return [
        {
          name: 'tipo',
          label: 'Tipo',
          type: 'select',
          required: true,
          options: tipos.map((t) => ({
            label: t.nombre,
            value: t.id_ticket_tipo,
          })),
        },
        { name: 'codigo', label: 'Codigo', type: 'text', required: true },
        { name: 'nombre', label: 'Nombre', type: 'text', required: true },
        { name: 'activa', label: 'Activa', type: 'switch' },
        { name: 'aplica_masivos', label: 'Aplica para Masivos', type: 'switch' },
        { name: 'aplica_cierre', label: 'Aplica para cierre', type: 'switch' },
      ];
    case 'subcategorias':
      return [
        {
          name: 'categoria',
          label: 'Categoria',
          type: 'select',
          required: true,
          options: categorias.map((c) => ({
            label: c.nombre,
            value: c.id_ticket_categoria,
          })),
        },
        { name: 'codigo', label: 'Codigo', type: 'text', required: true },
        { name: 'nombre', label: 'Nombre', type: 'text', required: true },
        { name: 'activa', label: 'Activa', type: 'switch' },
        { name: 'aplica_masivos', label: 'Aplica para Masivos', type: 'switch' },
      ];
    default:
      return [];
  }
};

const getDefaultValues = (catalogType: CatalogType): Record<string, any> => {
  switch (catalogType) {
    case 'estados':
      return {
        codigo: '',
        nombre: '',
        color: '#000000',
        icono: '',
        orden: 0,
        es_inicial: false,
        es_final: false,
        activo: true,
      };
    case 'prioridades':
      return {
        codigo: '',
        nombre: '',
        sla_minutos: 0,
        color: '#000000',
        icono: '',
        orden: 0,
      };
    case 'areas':
      return {
        codigo: '',
        nombre: '',
        descripcion: '',
        color: '#000000',
        orden: 0,
        activa: true,
      };
    case 'canales':
      return { codigo: '', nombre: '', icono: '', activo: true };
    case 'tipos':
      return {
        codigo: '',
        nombre: '',
        descripcion: '',
        requiere_visita_default: false,
        activo: true,
        aplica_masivos: false,
      };
    case 'categorias':
      return { tipo: '', codigo: '', nombre: '', activa: true, aplica_masivos: false, aplica_cierre: false };
    case 'subcategorias':
      return { categoria: '', codigo: '', nombre: '', activa: true, aplica_masivos: false };
    default:
      return {};
  }
};

const FormCatalogo: React.FC<FormCatalogoProps> = ({
  open,
  onClose,
  catalogType,
  initialData,
  onSubmit,
  isLoading,
  tipos = [],
  categorias = [],
  areas = [],
  prefill,
}) => {
  const isEditing = !!initialData;
  const title = `${isEditing ? 'Editar' : 'Crear'} ${CATALOG_LABELS[catalogType]}`;
  const fields = getFieldsConfig(catalogType, tipos, categorias, areas);
  const defaults = { ...getDefaultValues(catalogType), ...(prefill || {}) };

  // Cuando se edita un Tipo existente, el backend devuelve area como un
  // objeto anidado ({id_area, nombre, codigo, ...}). Lo aplastamos a
  // area_id (string UUID) para que el select del form lo seleccione bien.
  const normalizeInitialData = (data: any): any => {
    if (!data || catalogType !== 'tipos') return data;
    const flattened = { ...data };
    if (flattened.area && typeof flattened.area === 'object') {
      flattened.area_id = flattened.area.id_area ?? flattened.area_id ?? '';
    }
    return flattened;
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: normalizeInitialData(initialData) ?? defaults,
  });

  useEffect(() => {
    if (open) {
      reset(normalizeInitialData(initialData) ?? defaults);
    }
  }, [open, initialData]);

  const handleFormSubmit = (data: any) => {
    // Para Tipos, transformar area_id (string del select) en el shape
    // anidado que el backend espera: area: { id_area: "<uuid>" }.
    // Si area_id es vacio, omitimos el field para que backend lo trate
    // como "no asignar/dejar igual".
    if (catalogType === 'tipos') {
      const payload: any = { ...data };
      const areaId = payload.area_id;
      delete payload.area_id;
      if (areaId) {
        payload.area = { id_area: areaId };
      } else if (isEditing) {
        // En edicion sin area seleccionada, no enviamos el campo para
        // que el backend no lo toque (no soportamos "desasignar" en este
        // PUT — eso requiere endpoint aparte).
      }
      onSubmit(payload);
      return;
    }
    onSubmit(data);
  };

  const renderField = (field: FieldConfig) => {
    if (field.type === 'switch') {
      return (
        <Grid item {...gridSizeMdLg6} key={field.name}>
          <Controller
            name={field.name}
            control={control}
            render={({ field: controllerField }) => (
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Switch
                  checked={controllerField.value || false}
                  onChange={(e) => controllerField.onChange(e.target.checked)}
                />
                <Typography variant="body2" sx={{ width: '100%' }}>
                  {field.label}
                </Typography>
              </Box>
            )}
          />
        </Grid>
      );
    }

    if (field.type === 'color') {
      return (
        <Grid item {...gridSizeMdLg6} key={field.name}>
          <Controller
            name={field.name}
            control={control}
            render={({ field: controllerField }) => (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {field.label}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="color"
                    value={controllerField.value || '#000000'}
                    onChange={(e) => controllerField.onChange(e.target.value)}
                    style={{ width: 48, height: 36, border: 'none', cursor: 'pointer' }}
                  />
                  <TextField
                    size="small"
                    value={controllerField.value || ''}
                    onChange={(e) => controllerField.onChange(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                </Box>
              </Box>
            )}
          />
        </Grid>
      );
    }

    if (field.type === 'select') {
      return (
        <Grid item {...gridSizeMdLg6} key={field.name}>
          <Controller
            name={field.name}
            control={control}
            rules={field.required ? { required: 'Este campo es requerido' } : undefined}
            render={({ field: controllerField }) => (
              <TextField
                select
                fullWidth
                label={field.label}
                value={controllerField.value || ''}
                onChange={controllerField.onChange}
                error={!!errors[field.name]}
                helperText={errors[field.name]?.message as string}
                required={field.required}
              >
                {(field.options || []).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
      );
    }

    return (
      <Grid item {...gridSizeMdLg6} key={field.name}>
        <Controller
          name={field.name}
          control={control}
          rules={field.required ? { required: 'Este campo es requerido' } : undefined}
          render={({ field: controllerField }) => (
            <TextField
              fullWidth
              label={field.label}
              type={field.type === 'number' ? 'number' : 'text'}
              value={controllerField.value ?? ''}
              onChange={(e) => {
                const val =
                  field.type === 'number'
                    ? e.target.value === ''
                      ? ''
                      : Number(e.target.value)
                    : e.target.value;
                controllerField.onChange(val);
              }}
              error={!!errors[field.name]}
              helperText={errors[field.name]?.message as string}
              required={field.required}
            />
          )}
        />
      </Grid>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={3}
        pt={2}
        pb={1}
      >
        <DialogTitle sx={{ p: 0, fontWeight: 600, fontSize: 20, flex: 1 }}>
          {title}
        </DialogTitle>
        <IconButton onClick={onClose} size="small" sx={{ ml: 2 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent dividers sx={{ px: 3, py: 2 }}>
          <Grid container spacing={2}>
            {fields.map((f) => renderField(f))}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit" disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={18} /> : null}
          >
            Guardar
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FormCatalogo;
